import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Printer } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { getInterventionPhotoUrl } from './api';
import { useInterventionReport } from './hooks';
import { InterventionReportPrintView } from './InterventionReportPrintView';
import type { InterventionReport } from './types';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return <div className="border-b py-3 last:border-0"><dt className="text-xs font-medium text-muted-foreground">{label}</dt><dd className="mt-1 whitespace-pre-wrap text-sm">{value}</dd></div>;
}

function ReportContent({ report, photoUrls, retryPhoto }: {
  report: InterventionReport;
  photoUrls: Record<string, string>;
  retryPhoto: (photoId: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 rounded-lg border p-4 sm:grid-cols-2">
        <div><p className="text-xs text-muted-foreground">Account</p><p className="font-medium">{report.sensorSnapshot.account.name} · {report.sensorSnapshot.account.accountNumber}</p></div>
        <div><p className="text-xs text-muted-foreground">Sensor</p><p className="font-medium">{report.sensorSnapshot.sensor.name}</p><p className="font-mono text-xs text-muted-foreground">{report.sensorSnapshot.sensor.macId || '—'}</p></div>
        <div><p className="text-xs text-muted-foreground">Intervention</p><p className="font-medium">{new Date(report.interventionAt).toLocaleString()}</p></div>
        <div><p className="text-xs text-muted-foreground">Technician</p><p className="font-medium">{report.createdByName}</p></div>
      </div>
      <dl className="rounded-lg border px-4">
        <Field label="Reason" value={report.reasonCategory.replace(/_/g, ' ')} />
        <Field label="Observations" value={report.observations} />
        <Field label="Diagnosis" value={report.diagnosis} />
        <Field label="Work performed" value={report.workPerformed} />
        <Field label="Outcome" value={<Badge variant="outline">{report.outcome.replace(/_/g, ' ')}</Badge>} />
        <Field label="Follow-up" value={report.followUpAction} />
        <Field label="Follow-up due" value={report.followUpDueAt ? new Date(report.followUpDueAt).toLocaleString() : null} />
      </dl>
      <section><h3 className="font-semibold">Frozen sensor snapshot</h3><div className="mt-3 grid gap-3 rounded-lg bg-muted/40 p-4 text-sm sm:grid-cols-2"><p>Reading: <strong>{report.sensorSnapshot.reading.value ?? '—'} {report.sensorSnapshot.reading.unit || ''}</strong></p><p>Reporting: <strong>{report.sensorSnapshot.health.freshness.replace(/_/g, ' ')}</strong></p><p>Battery: <strong>{report.sensorSnapshot.health.transmitter.batteryPercentage ?? '—'}%</strong></p><p>Signal: <strong>{report.sensorSnapshot.health.transmitter.rssi ?? '—'}</strong></p><p>Calibration due: <strong>{report.sensorSnapshot.calibration.due || '—'}</strong></p><p>Captured: <strong>{new Date(report.sensorSnapshot.capturedAt).toLocaleString()}</strong></p></div></section>
      <section><h3 className="font-semibold">Photos</h3>{report.photos.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No photos attached.</p> : <div className="mt-3 grid grid-cols-2 gap-3">{report.photos.map((photo) => photoUrls[photo.id] ? <img key={photo.id} src={photoUrls[photo.id]} alt={photo.originalFilename} onError={() => retryPhoto(photo.id)} className="h-48 w-full rounded-lg border object-contain" /> : <div key={photo.id} className="flex h-48 items-center justify-center rounded-lg border bg-muted text-sm text-muted-foreground">Loading {photo.originalFilename}…</div>)}</div>}</section>
      <section><h3 className="font-semibold">Corrections</h3>{report.addenda.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No corrections.</p> : <ol className="mt-3 space-y-3">{report.addenda.map((addendum) => <li key={addendum.id} className="border-l-2 pl-3"><p className="whitespace-pre-wrap text-sm">{addendum.body}</p><p className="mt-1 text-xs text-muted-foreground">{addendum.createdByName} · {new Date(addendum.createdAt).toLocaleString()}</p></li>)}</ol>}</section>
    </div>
  );
}

export function InterventionReportDetailSheet({ accountId, reportId, onClose }: {
  accountId: string;
  reportId: string | null;
  onClose: () => void;
}) {
  const query = useInterventionReport(accountId, reportId);
  const report = query.data;
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const photoSignature = report?.photos.map((photo) => photo.id).join(',') || '';

  const loadPhoto = useCallback(async (photoId: string) => {
    if (!reportId) return;
    try {
      const url = await getInterventionPhotoUrl(accountId, reportId, photoId);
      setPhotoUrls((current) => ({ ...current, [photoId]: url }));
    } catch {
      setPhotoUrls((current) => {
        const next = { ...current };
        delete next[photoId];
        return next;
      });
    }
  }, [accountId, reportId]);

  useEffect(() => {
    setPhotoUrls({});
    if (!reportId || !photoSignature) return;
    photoSignature.split(',').forEach((photoId) => { void loadPhoto(photoId); });
  }, [loadPhoto, photoSignature, reportId]);

  return (
    <>
      <Sheet open={reportId !== null} onOpenChange={(open) => !open && onClose()}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl print:hidden">
          <SheetHeader>
            <SheetTitle>Intervention report</SheetTitle>
            <SheetDescription>{report?.reportNumber || 'Submitted field-service record'}</SheetDescription>
          </SheetHeader>
          <div className="my-5 flex justify-end"><Button variant="outline" onClick={() => window.print()} disabled={!report}><Printer className="mr-2 h-4 w-4" />Print / Save PDF</Button></div>
          {query.isLoading ? <div className="space-y-3">{Array.from({ length: 6 }, (_, index) => <Skeleton key={index} className="h-12 w-full" />)}</div>
            : query.isError ? <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"><AlertCircle className="h-4 w-4" />Report could not be loaded.</div>
              : report ? <ReportContent report={report} photoUrls={photoUrls} retryPhoto={(photoId) => void loadPhoto(photoId)} /> : null}
        </SheetContent>
      </Sheet>
      {report && <InterventionReportPrintView report={report} photoUrls={photoUrls} />}
    </>
  );
}
